import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { HasPermissionDirective } from '@ipg/ngx-frontend-toolset';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import {
  AiAgentSettingsResponse,
  AiAgentSettingsService,
} from '../../services/ai-agent-settings.service';

type AgentKey = 'interaction_creator' | 'interaction_auditor';

@Component({
  selector: 'app-settings-ai-agents',
  imports: [
    MatButton,
    MatIcon,
    MatFormField,
    MatSelect,
    MatOption,
    MatProgressSpinner,
    ReactiveFormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './settings-ai-agents.component.html',
  styleUrl: './settings-ai-agents.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsAiAgentsComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _settingsService = inject(AiAgentSettingsService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _route = inject(ActivatedRoute);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly activeAgent = signal<AgentKey>('interaction_creator');
  protected readonly settingsData = signal<AiAgentSettingsResponse | null>(null);

  protected readonly form = this._fb.nonNullable.group({
    creatorModel: '',
    auditorModel: '',
  });

  protected readonly availableModels = computed(
    () => this.settingsData()?.availableModels ?? []
  );

  protected readonly creatorDetail = computed(
    () => this.settingsData()?.agents?.['interaction_creator']
  );

  protected readonly auditorDetail = computed(
    () => this.settingsData()?.agents?.['interaction_auditor']
  );

  protected readonly activeDetail = computed(() =>
    this.activeAgent() === 'interaction_creator'
      ? this.creatorDetail()
      : this.auditorDetail()
  );

  protected readonly activeControl = computed(() =>
    this.activeAgent() === 'interaction_creator'
      ? this.form.controls.creatorModel
      : this.form.controls.auditorModel
  );

  private readonly _creatorModel = toSignal(
    this.form.controls.creatorModel.valueChanges,
    { initialValue: '' }
  );

  private readonly _auditorModel = toSignal(
    this.form.controls.auditorModel.valueChanges,
    { initialValue: '' }
  );

  protected readonly activeSelectedModel = computed(() =>
    this.activeAgent() === 'interaction_creator'
      ? this._creatorModel()
      : this._auditorModel()
  );

  protected readonly activeSystemPrompt = computed(() => {
    const detail = this.activeDetail();
    if (!detail) return '';
    const selectedModel = this.activeSelectedModel();
    if (detail.systemPrompts && selectedModel && detail.systemPrompts[selectedModel]) {
      return detail.systemPrompts[selectedModel];
    }
    return detail.systemPrompt;
  });

  protected readonly hasChanges = computed(() => {
    const data = this.settingsData();
    if (!data) return false;
    if (this.activeAgent() === 'interaction_creator') {
      const current = this._creatorModel();
      const orig = data.agents?.['interaction_creator']?.model;
      return Boolean(current && current !== orig);
    } else {
      const current = this._auditorModel();
      const orig = data.agents?.['interaction_auditor']?.model;
      return Boolean(current && current !== orig);
    }
  });

  ngOnInit(): void {
    const snapshotAgent = this._route.snapshot.data['agent'] as AgentKey | undefined;
    if (snapshotAgent) {
      this.activeAgent.set(snapshotAgent);
    }

    this._route.data.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((data) => {
      const agent = data['agent'] as AgentKey | undefined;
      if (agent) {
        this.activeAgent.set(agent);
      }
    });

    this.loadSettings();
  }

  protected loadSettings(): void {
    this.loading.set(true);
    this._settingsService
      .getSettings()
      .pipe(
        tap((data) => {
          this.settingsData.set(data);
          this.form.patchValue({
            creatorModel: data.agents?.['interaction_creator']?.model ?? '',
            auditorModel: data.agents?.['interaction_auditor']?.model ?? '',
          });
        }),
        catchError(() => {
          this._snackbar.open(
            'Kon de AI-agent instellingen niet ophalen. Probeer het later nog eens.',
            'OK',
            { duration: 5000 }
          );
          return EMPTY;
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe();
  }

  protected resetActiveToDefault(): void {
    const detail = this.activeDetail();
    if (detail) {
      this.activeControl().setValue(detail.defaultModel);
      this.activeControl().markAsDirty();
    }
  }

  protected saveSettings(): void {
    if (this.saving() || !this.hasChanges()) return;

    this.saving.set(true);

    // Only the agent on screen is sent. PATCHing both from the page-load
    // snapshot would silently revert a concurrent change to the other agent.
    const agent = this.activeAgent();
    const model = this.activeControl().value;

    this._settingsService
      .updateSettings({ agents: { [agent]: { model } } })
      .pipe(
        tap((updated) => {
          this.settingsData.set(updated);
          this.form.reset({
            creatorModel: updated.agents?.['interaction_creator']?.model ?? '',
            auditorModel: updated.agents?.['interaction_auditor']?.model ?? '',
          });
          this._snackbar.open('Instellingen opgeslagen', 'OK', {
            duration: 4000,
          });
        }),
        catchError(() => {
          this._snackbar.open(
            'Opslaan mislukt. Controleer uw invoer en rechten.',
            'OK',
            { duration: 5000 }
          );
          return EMPTY;
        }),
        finalize(() => this.saving.set(false))
      )
      .subscribe();
  }
}
